import { ref } from 'vue';
import type { InjectionKey } from 'vue';
import { find, filter, some } from 'lodash-es';

type Modal = {
  activeName: string;
  params?: Record<string, unknown>;
  isActive: boolean;
};

type ShowOption = {
  params?: Record<string, unknown>;
};

export interface ModalController {
  isVisible(name: string): boolean;
  show(name: string, options?: ShowOption): void;
  hide(name: string): void;
  getParams<T>(name: string): T | undefined;
}

export const ModalControllerKey: InjectionKey<ModalController> =
  Symbol('MODAL_SYMBOL');

export function createModalController(): ModalController {
  const modalStack = ref<Array<Modal>>([]);

  function getModalByName(name: string) {
    return find(modalStack.value, ['activeName', name]);
  }

  function isVisible(name: string) {
    return some(modalStack.value, modal => modal.activeName === name);
  }

  function show(name: string, { params = {} }: ShowOption = {}) {
    if (isVisible(name)) {
      return;
    }

    modalStack.value.push({
      activeName: name,
      params,
      isActive: true,
    });
  }

  function getParams<T>(name: string): T | undefined {
    const modal = getModalByName(name);
    return modal ? (modal.params as T) : undefined;
  }

  function removeModalByName(name: string) {
    modalStack.value = filter(modalStack.value, m => m.activeName !== name);
  }

  function hide(name: string) {
    removeModalByName(name);
  }

  return {
    isVisible,
    show,
    getParams,
    hide,
  };
}
